import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Tracks a media query after hydration; server and first client render report `false`. */
export function useMediaQueryMatch(query: string): boolean {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const update = (): void => setMatches(list.matches);
    update();
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', update);
      return () => list.removeEventListener('change', update);
    }
    list.addListener(update);
    return () => list.removeListener(update);
  }, [query]);
  return matches;
}

const fill = `
  border: 0;
  display: block;
  height: 100%;
  inset: 0;
  position: absolute;
  width: 100%;
`;

const LoopVideo = styled.video`
  ${fill}
  background: transparent;
  object-fit: cover;
  object-position: var(--cms-decorative-loop-position, center);
  pointer-events: none;
`;

/** Same fit as the looping video so the poster-to-video swap never jumps. */
const LoopPoster = styled.img`
  ${fill}
  object-fit: cover;
  object-position: var(--cms-decorative-loop-position, center);
`;

type DecorativeLoopProps = {
  src: string;
  mimeType: string;
  posterSrc: string;
  testId?: string;
};

/**
 * Decorative ambient loop shared by Video (decorative mode) and Hero (background video). It fills
 * its positioned parent, so it never changes the parent's size. The poster is the first paint and the
 * only thing shown for reduced motion and phones; the video element is created only once it is
 * allowed and near the viewport. It is always muted, looping, inline and without controls.
 */
export function DecorativeLoop({
  src,
  mimeType,
  posterSrc,
  testId = 'cms-video-decorative',
}: DecorativeLoopProps): JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const sourceKey = `${src}|${mimeType}`;
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const reducedMotion = useMediaQueryMatch(REDUCED_MOTION_QUERY);
  const phone = useMediaQueryMatch(mediaQueries.largePhone);
  const [nearViewport, setNearViewport] = useState(false);
  const allowed = !reducedMotion && !phone && failedKey !== sourceKey;
  // Editing an existing <source> never restarts the browser's media selection, so a replaced asset
  // needs a new element, and a failure belongs to the source that failed, not to the component.

  useEffect(() => {
    if (!allowed || nearViewport) return undefined;
    const frame = frameRef.current;
    if (!frame || typeof IntersectionObserver !== 'function') {
      setNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNearViewport(true);
      },
      { rootMargin: '200px' }
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, [allowed, nearViewport]);

  return (
    <div ref={frameRef} style={{ position: 'absolute', inset: 0 }} data-testid={testId}>
      <LoopPoster src={posterSrc} alt='' />
      {allowed && nearViewport ? (
        <LoopVideo
          key={sourceKey}
          autoPlay
          muted
          loop
          playsInline
          preload='metadata'
          poster={posterSrc}
          tabIndex={-1}
          disablePictureInPicture
        >
          <source src={src} type={mimeType} onError={() => setFailedKey(sourceKey)} />
        </LoopVideo>
      ) : null}
    </div>
  );
}
